package fr.frux.shared.data.repository

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.config.appVersion
import fr.frux.core.common.di.AppScope
import fr.frux.core.common.logging.Logger
import fr.frux.core.common.platform.platformId
import fr.frux.core.common.result.runCatchingCancellable
import fr.frux.core.network.api.FeedbackApi
import fr.frux.core.network.error.mapFailureToAppError
import fr.frux.shared.domain.model.FeedbackCategory
import fr.frux.shared.domain.model.FeedbackDraft
import fr.frux.shared.domain.repository.FeedbackRepository
import fr.frux.shared.dto.CreateFeedbackRequest
import fr.frux.shared.dto.FeedbackCategoryDto

/**
 * Joint la version et la plateforme au brouillon (#437), sur le patron de `SupportRepositoryImpl` :
 * les deux valeurs sont des défauts de constructeur, substituables en test.
 */
@ContributesBinding(AppScope::class)
@SingleIn(AppScope::class)
@Inject
class FeedbackRepositoryImpl(
    private val api: FeedbackApi,
    private val version: String = appVersion(),
    private val platform: String = platformId,
) : FeedbackRepository {
    override suspend fun send(draft: FeedbackDraft): Result<Unit> =
        runCatchingCancellable {
            api.sendFeedback(
                CreateFeedbackRequest(
                    category = draft.category.toDto(),
                    message = draft.message,
                    appVersion = version,
                    platform = platform,
                    sourceScreen = draft.sourceScreen,
                ),
            )
        }
            // Ni l'exception ni son message : le texte saisi pourrait y transiter, et Logger.e part
            // vers Sentry. Le ViewModel décide si l'échec est attendu ou non.
            .onFailure { Logger.d(TAG, "Envoi d'avis en échec (${it::class.simpleName})") }
            .mapFailureToAppError()

    private fun FeedbackCategory.toDto(): FeedbackCategoryDto =
        when (this) {
            FeedbackCategory.BUG -> FeedbackCategoryDto.BUG
            FeedbackCategory.IDEA -> FeedbackCategoryDto.IDEA
            FeedbackCategory.OTHER -> FeedbackCategoryDto.OTHER
        }

    private companion object {
        const val TAG = "FeedbackRepository"
    }
}
